import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import GIButton from '@components/common/button/GIButton';
import { configs } from '@constants/configs/configs';
import AlertInfo from '@components/common/alert/AlertInfo';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import { BREAK_POINTS } from '@constants/common/breakpoints';
import { cleanQueryParams } from '@utils/miscellaneous/urlSearchParams';
import ParsedFormattedMessage from '@utils/miscellaneous/react-intl-utlis';
import useAppSelector from '@hooks/useAppSelector';
import { environmentSelector } from '@selectors/commonSelectors';
import { IUserInfo } from '@modules/ridp/types/ridpTypes';

const StyledFinishContainer = styled.div`
    .gi-alert__info {
        margin-bottom: 24px !important;
    }
`;
const StyledButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;

    @media (max-width: ${BREAK_POINTS.sm}) {
        flex-direction: column;
    }

    .gi-button {
        margin: 30px 20px 30px;
        @media (max-width: ${BREAK_POINTS.sm}) {
            margin: 10px 20px 10px !important;
        }
    }

    .gi-button:disabled {
        background-color: ${({ theme }) => theme.gray4};
    }
`;

const StyledContinueButton = styled.div`
    display: flex;
    justify-content: flex-end;

`;

const StyledNote = styled.div`
    padding: 20px 0px;
`;

const TechincalErrorPage = ({ backToApplication }) => {
    const {
        routes: { RIDP_UPLOAD_DOCUMENT },
    } = configs;

    const navigate = useNavigate();
    const location = useLocation();
    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo) as IUserInfo;
    const isRidpRbaEnabled = currentUserInfo?.isRidpRbaEnabled === true;
    const attemptsLeft =
        isRidpRbaEnabled && currentUserInfo?.totalAttemptsAllowed != null && currentUserInfo?.primaryAttempts != null
            ? currentUserInfo.totalAttemptsAllowed - currentUserInfo.primaryAttempts
            : 0;
    const isVerifyDisabled = isRidpRbaEnabled
        ? (currentUserInfo?.primaryAttempts >= currentUserInfo?.totalAttemptsAllowed || currentUserInfo?.timePeriodBlock)
        : currentUserInfo?.sixteenHourBlock === true;

    const proceedToManualVerification = () => {
        const existingParams = new URLSearchParams(location.search);
        navigate(`${RIDP_UPLOAD_DOCUMENT}?${existingParams.toString()}`);
    };
    return (
        <main id="main-content" className="gi-panel">
            <StyledFinishContainer>
                <h1 className="usa-sr-only">
                    <FormattedMessage id="ridp.sidebar.finish" />
                    &nbsp;:&nbsp;
                    <FormattedMessage id="ridp.sidebar.header" />
                </h1>
                <ParsedFormattedMessage id="ridp.finish.technicalerror.para1"
                    values={{
                        isRidpRbaEnabled,
                        attempts: attemptsLeft >= 0 ? attemptsLeft : 0,
                        timePeriod: currentUserInfo.timePeriod,
                        totalAttempts: currentUserInfo.totalAttemptsAllowed
                    }} />
                <StyledButtonContainer>
                    <GIButton
                        type="button"
                        id="backToApplication"
                        labelId="ridp.finish.button1.label"
                        onClickHandler={backToApplication}
                        isDisabled={isVerifyDisabled}
                    />
                    <FormattedMessage id="OR" />
                    <GIButton
                        type="button"
                        id="ridpManualVerification"
                        labelId="ridp.finish.button2.label"
                        onClickHandler={proceedToManualVerification}
                    />
                </StyledButtonContainer>
                <StyledNote>
                    <FormattedMessage
                        id="ridp.finish.technicalerror.para2"
                        values={{
                            br: <br />,
                            strong: chunks => <strong>{chunks}</strong>,
                        }}
                    />
                </StyledNote>
            </StyledFinishContainer>
        </main>
    );
};

const FinishTabContent = ({ backToApplication }) => {

    const { routes: { RIDP } } = configs;
    const currentUserInfo = useAppSelector(state => state.ridp.currentUserInfo) as IUserInfo;
    const { exchangeName } = useAppSelector(environmentSelector);
    const navigate = useNavigate();
    const location = useLocation();
    const existingParams = new URLSearchParams(location.search);

    useEffect(() => {
        if (currentUserInfo?.ridpVerified !== 'Y') {
            navigate(`${RIDP}?${existingParams.toString()}`);
        }
    }, [currentUserInfo]);

    return (
        <main id="main-content" className="gi-panel">
            <StyledFinishContainer>
                <h1 className="usa-sr-only">
                    <FormattedMessage id="ridp.sidebar.finish" />
                    &nbsp;:&nbsp;
                    <FormattedMessage id="ridp.sidebar.header" />
                </h1>
                <AlertInfo id="ridp.finish.alert.header" />
                <FormattedMessage id="ridp.finish.para" values={{ exchangeName }} />
                <StyledContinueButton>
                    <GIButton
                        type="button"
                        id="finish"
                        labelId="ridp.contactinfo.input.label.continue"
                        onClickHandler={backToApplication}
                    />
                </StyledContinueButton>
            </StyledFinishContainer>
        </main>
    );
};

const Finish = () => {
    const {
        routes: { RIDP_TECHNICAL_ERROR, SSAP, RIDP_VERIFICATION_COMPLETE },
    } = configs;
    const location = useLocation();
    const backToApplication = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const existingParams = cleanQueryParams(queryParams);
        const coverageYear = existingParams['coverageYear'];
        const applicationType = existingParams['applicationType'];
        const caseNumber = existingParams['caseNumber'];
        if (caseNumber) {
            window.location.href = `${SSAP}?caseNumber=${caseNumber}`;
        } else if (coverageYear && applicationType) {
            window.location.href = `${SSAP}?applicationType=${applicationType}&coverageYear=${coverageYear}`;
        } else {
            window.location.href = `${SSAP}`;
        }
    };
    return (
        <>
            <ConditionalRendering isTrue={location.pathname === RIDP_TECHNICAL_ERROR}>
                <TechincalErrorPage backToApplication={backToApplication} />
            </ConditionalRendering>
            <ConditionalRendering isTrue={location.pathname === RIDP_VERIFICATION_COMPLETE}>
                <FinishTabContent backToApplication={backToApplication} />
            </ConditionalRendering>
        </>
    );
};

export default Finish;
